import ModelEntity from "../model-entity"
import DateTimeUtils from "../../gd/utils/datetime-utils"
import DocumentProperties from "./properties"
import DocumentHistory from "./history"
import DocumentPages from "./pages/pages"
import {ITEM_TYPE_DOCUMENT} from "../../gd/const/item"
import ApiDocument from "../../gd/api/document"
import {Access} from "../../gd/const/access"
import {Cover} from "../task/properties/cover"
import {DocumentSystemType} from "./types"
import ErrorProto from "../../gd/validation/error";
import ErrorFieldRequired from "../../gd/validation/error-field-required";
import DocFields from "./fields";
import RelationsCollection from "../relation/collection"

// const debug = "Document"
const debug = false

export default class Document extends ModelEntity {

    public name             : string
    public noteTypeId       : ID
    public projectId        : ID
    public systemType       : DocumentSystemType

    public momentCreated    : Moment | null
    public momentUpdated    : Moment | null

    public createdByUserId  : ID
    public editors          : ID[]
    public accessRead       : Access
    public accessWrite      : Access

    public content          : string
    public contentRTF       : any

    public isDeleted        : boolean

    public historyCount     : number
    public history          : DocumentHistory
    public properties       : DocumentProperties

    public pages!           : DocumentPages

    public relations        : RelationsCollection

    constructor(data: any) {
        super(data['id'], ITEM_TYPE_DOCUMENT);

        // this.id                 = data['id']
        // this.rid                = 'd-'+data['id']

        this.name               = data['name']
        this.noteTypeId         = data['noteTypeId']
        this.projectId          = data['projectId']
        this.systemType         = data['systemType']

        this.momentCreated      = data['momentCreated'] ? DateTimeUtils.datetimeToMoment(data['momentCreated']) : null
        this.momentUpdated      = data['momentUpdated'] ? DateTimeUtils.datetimeToMoment(data['momentUpdated']) : null

        this.createdByUserId    = data['createdByUserId']
        this.editors            = data['editors']
        this.accessRead         = _.get(data,'accessRead',gd.const.project.access.STANDARD)
        this.accessWrite        = _.get(data,'accessWrite',gd.const.project.access.STANDARD)

        this.content            = _.get(data,'content','')
        this.contentRTF         = data['contentRTF']

        this.isDeleted          = _.get(data,'isDeleted', false)

        this.historyCount       = data['historyCount'] // is used in documents list
        this.history            = new DocumentHistory(data['history'])
        this.properties         = new DocumentProperties(data['properties'])
        this._parsePages()

        // Like `history`, a sibling of the document in the view payload that the
        // loader folds into `data`. Built unconditionally -- the popover needs a
        // collection to add the first item into.
        this.relations          = RelationsCollection.initFromJson(data['relatedItems'], this)

        // this.projectAccess      = data['projectAccess'] //no usage found

        debug && console.log(debug, "constructor", this)
    }

    get isPage():boolean {
        return this.systemType === DocumentSystemType.PAGE
    }

    get isWhiteboard():boolean {
        return this.systemType === DocumentSystemType.WHITEBOARD
    }

    get isWhiteboardResource():boolean {
        return this.systemType === DocumentSystemType.WHITEBOARD_RESOURCE
    }

    get createdByUser()     {
        const userId = this.history.first?.userId || this.createdByUserId
        return gd.session.users.get(userId)
    }

    get lastUpdatedByUser()     {
        const userId = this.history.last?.userId || this.createdByUserId
        return gd.session.users.get(userId)
    }

    get lastUpdated()       {return this.momentUpdated /*this.history[0] && this.history[0].moment*/}
    get lastViewed()        {return this.properties.lastViewed || this.momentUpdated}
    get projectFullPath()   {return this.projectId}
    get pagesCount()        {return this.properties.pages}

    get cover()             {return this.properties.cover}

    set cover(value: Cover | undefined) {
        this.properties.cover = value
    }

    /*get canView() {
        const project = gd.session.projects.get(this.projectId)
        return gd.user.id === this.createdByUserId || project.access >= this.accessRead
    }*/

    get canEdit() {
        const project = gd.session.projects.get(this.projectId)!
        return gd.user.id === this.createdByUserId || project.access >= this.accessWrite
    }

    get canBeDeleted() {
        return this.systemType === DocumentSystemType.DEFAULT || this.systemType === DocumentSystemType.WHITEBOARD
    }

    get project() {
        return gd.session.projects.get(this.projectId)
    }

    _parsePages = () => {
        if (Array.isArray(this.contentRTF)) {
            if (this.contentRTF.length === 1) this.contentRTF[0].title = null //this.name
            this.pages = new DocumentPages(this.contentRTF, this)
        } else {
            this.pages = new DocumentPages([{
                title       : null, //this.name,
                content     : this.content,
                contentRTF  : this.contentRTF
            }], this)
        }
    }

    loadRevisionContent = (noteData: any) => {
        this.content    = _.get(noteData, 'content','')
        this.contentRTF = _.get(noteData, 'contentRTF', null)

        this._parsePages()
    }

    _isSameValue(paramName: string, value: any) {
        // @ts-ignore
        const paramValue = this[paramName]
        if (moment.isMoment(paramValue)) {
            // console.log("isSameValue", DatetimeUtils.momentToDate(this[paramName]), DatetimeUtils.dateTruncTime(value), DatetimeUtils.isSame(this[paramName], DatetimeUtils.dateToMoment(value), 'day'))
            // @ts-ignore
            return DatetimeUtils.isSame(paramValue, DatetimeUtils.datetimeToMoment(value), 'second')
        } else {
            return _.isEqual(paramValue,value)
        }
    }

    applyDelta(data: any) {
        debug && console.log(debug, "applyDelta()", data)

        let isChanged = false

        _.mapKeys(data, (value ,key)=>{
            if (['id'].includes(key)) return

            debug && console.log("applyDelta key=", key, "value=", value)

            switch (key) {
                case 'momentCreated':
                case 'momentUpdated': value = DateTimeUtils.datetimeToMoment(value); break

                case 'history'      : value = new DocumentHistory(value); break
                case 'properties'   : value = new DocumentProperties(value); break
            }

            // @ts-ignore
            debug && console.log("applyDelta _isSameValue", this._isSameValue(key, value), this[key], value)

            if (!this._isSameValue(key, value)) {
                // @ts-ignore
                this[key] = value

                isChanged = true
                this.updatedFields.delete(key)
            }
        });

        this._parsePages()

        debug && console.log("....applyDelta isChanged", isChanged)

        return isChanged
    }

    markAsSaved = () => {
        // when we save note we need to mark all attachments as saved...
        _.map(this.pages.models, page=>{
            if (page.attachmentsCollection) {
                page.attachmentsCollection.markAllAsSaved()
            }
        })
    }

    goTo() {

        // Eto hitrii hack on otkrivaet vsegda structure page
        // ili project view page
        if (this.isWhiteboardResource) {
            gd.navigation.goToProject(this.projectId, "/whiteboard/"+this.id)
            return
        }

        if (this.isPage) {
            gd.navigation.goToProject(this.projectId, "/page/"+this.id)
            return
        }

        // @ts-ignore
        gd.noteView.open(this.id)
    }

    /*async save() {

        const params = {}

        debug && console.log(debug,"save()", this.updatedFields)

        // this is for universal update
        this.updatedFields.forEach((fieldName) => {
            params[fieldName] = this[fieldName]
        })

        this.updatedFields.clear()

        if (params && _.size(params)>0) return await DocumentApi.update(this.id, params)

        return true
    }*/

    // EXPORT

    // The summary `documentsUds` returns, so `new Document(json)` takes it back.
    // Without it the model falls through to `ModelEntity`'s stub, which returns
    // `{}` and logs -- and `JSON.stringify` calls `toJSON()` at any depth, so a
    // document mention saved into a message RTF lost its whole payload.
    //
    // The body is deliberately out: a reference to a document is not the
    // document, and `save()` sends `pages.toJSON()` itself.
    toJSON(): JSONObject {
        return {
            id              : this.id,
            name            : this.name,
            projectId       : this.projectId,
            noteTypeId      : this.noteTypeId,
            systemType      : this.systemType,
            momentCreated   : DateTimeUtils.momentToUTCISODatetime(this.momentCreated),
            momentUpdated   : DateTimeUtils.momentToUTCISODatetime(this.momentUpdated),
        }
    }

    validate():ErrorProto[] | null  {

        const errors : any[] = []

        const fields = new DocFields()

        if (!this.name) errors.push(new ErrorFieldRequired(fields.name))
        if (!this.projectId) errors.push(new ErrorFieldRequired(fields.projectId))

        if (_.isEmpty(errors)) return null
        else return errors
    }

    async save() {
        await ApiDocument.save(this.id, {
            contentRTF  : this.pages.toJSON(),
            cover       : this.cover?.toJSON() || null
        }).promise()
        this.markAsSaved()
    }
}
